import { ArrowRight, CheckCircle2, Circle } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useEventStats, useInvitations } from '../../api/hooks'
import type { Event } from '../../api/types'
import { Card, DrillLink, Progress, Stat, TableSkeleton } from '../../components/ui'
import { num, pct } from '../../lib/format'

export function OverviewTab({ event: e, isManager }: { event: Event; isManager: boolean }) {
  const stats = useEventStats(e.id)
  const invs = useInvitations(e.id, isManager)
  const nav = useNavigate()
  const base = `/event/${e.id}`
  const s = stats.data
  const hasSent = (s?.sent ?? 0) > 0
  const steps = [
    { label: 'Event skapat', done: true, to: '' },
    { label: 'Kategorier genererade', done: e.counts.categories > 0, to: 'kategorier', hint: `${e.counts.categories} anmälningslänkar` },
    { label: 'Inbjudan byggd', done: (invs.data?.length ?? 0) > 0, to: 'inbjudningar' },
    { label: 'Utskick gjort', done: hasSent, to: 'inbjudningar', hint: hasSent ? `${num(s!.sent)} mottagare` : undefined },
    { label: 'Event aktiverat', done: e.status === 'AKTIVT' || e.status === 'STANGT' },
  ]

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Stat label="Anmälda" value={s ? s.registered : '–'} sub={s ? `${pct(s.fill_rate)} av ${num(s.capacity)} platser` : ''} tone="navy" to={`${base}/deltagare`} />
          <Stat label="Skickade" value={s ? s.sent : '–'} sub={s && s.cancelled ? `${s.cancelled} avanmälda` : 'inbjudningar'} delay={40} to={`${base}/utskick`} />
          <Stat label="Öppningsfrekvens" value={s ? s.open_rate * 100 : '–'} format={(n) => `${Math.round(n)} %`} sub={s ? `${num(s.opened)} öppnade` : ''} delay={80} to={`${base}/utskick?filter=opened`} />
          <Stat label="Anmälningsgrad" value={s ? s.registration_rate * 100 : '–'} format={(n) => `${Math.round(n)} %`} sub={s ? `${num(s.clicked)} klickade länken` : ''} delay={120} to={`${base}/utskick?filter=registered`} />
        </div>

        <Card title="Anmälningar per team" padded={false} className="animate-fade-up" style={{ animationDelay: '160ms' }}>
          {stats.isLoading ? <TableSkeleton rows={5} cols={6} /> : !s || s.teams.length === 0 ? (
            <p className="p-5 text-sm text-ink-500">Inga anmälningar eller utskick ännu.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="table-head"><tr><th>Team</th><th>Rådgivare</th><th className="text-right">Kund</th><th className="text-right">Prospekt</th><th className="text-right">Skickade</th><th className="text-right">Öppnade</th><th className="text-right">Anmälda</th><th className="w-40">Konvertering</th></tr></thead>
                <tbody>
                  {s.teams.map((t) => {
                    const total = t.kund_registered + t.prospekt_registered + t.manual_registered
                    const conv = t.sends ? t.registered_from_send / t.sends : 0
                    return (
                      <tr key={t.team_id ?? 'manual'} className="table-row cursor-pointer" onClick={() => nav(`${base}/deltagare${t.team_code ? `?team=${t.team_code}` : ''}`)} title="Visa teamets anmälningar">
                        <td className="font-medium text-ink-900">{t.team_code ? `${t.team_code} ${t.team_name}` : t.team_name}</td>
                        <td className="text-ink-500">{t.advisor_name ?? '–'}</td>
                        <td className="text-right tabular-nums">{t.kund_registered}</td>
                        <td className="text-right tabular-nums">{t.prospekt_registered}</td>
                        <td className="text-right tabular-nums">{t.sends && t.team_id ? <DrillLink to={`${base}/utskick?team_id=${t.team_id}`}>{t.sends}</DrillLink> : '–'}</td>
                        <td className="text-right tabular-nums">{t.opened && t.team_id ? <DrillLink to={`${base}/utskick?team_id=${t.team_id}&filter=opened`}>{t.opened}</DrillLink> : '–'}</td>
                        <td className="text-right font-semibold tabular-nums text-ink-900">{total}</td>
                        <td><div className="flex items-center gap-2"><Progress value={conv} className="flex-1" /><span className="w-10 text-right text-xs tabular-nums text-ink-500">{t.sends ? pct(conv) : '–'}</span></div></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="space-y-4">
        {isManager && (
          <Card title="Arbetsflöde">
            <ol className="space-y-3">
              {steps.map((st, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  {st.done ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-500" /> : <Circle className="mt-0.5 size-4 shrink-0 text-ink-300" />}
                  <div className="flex-1">
                    <div className={st.done ? 'text-ink-900' : 'text-ink-500'}>{st.label}</div>
                    {st.hint && <div className="text-xs text-ink-500">{st.hint}</div>}
                  </div>
                  {!st.done && st.to && <Link to={`/event/${e.id}/${st.to}`} className="text-navy-700 hover:text-navy-900"><ArrowRight className="size-4" /></Link>}
                </li>
              ))}
            </ol>
          </Card>
        )}
        <Card title="Om eventet">
          <dl className="space-y-2.5 text-sm">
            {e.description && <div><dt className="text-xs text-ink-500">Beskrivning</dt><dd className="leading-relaxed">{e.description}</dd></div>}
            <div><dt className="text-xs text-ink-500">Plats</dt><dd>{e.location}{e.address ? `, ${e.address}` : ''}</dd></div>
            <div><dt className="text-xs text-ink-500">Kapacitet</dt><dd>{num(e.capacity)} platser</dd></div>
            {e.created_by_name && <div><dt className="text-xs text-ink-500">Skapad av</dt><dd>{e.created_by_name}</dd></div>}
          </dl>
        </Card>
      </div>
    </div>
  )
}
